import { computed } from "vue";

import DashboardService, {
  ACTIVE_TREATMENT_FILTER_MAP,
  EActiveTreatmentFilter,
  EActiveTreatmentType,
} from "@/api/models/patient/dashboard";
import type {
  IPatientDashboardFilters,
  TPatientDashboardItem,
} from "@/api/models/patient/dashboard";
import type { TPatient } from "@/api/models/patient/patient";
import { EReferralStatus } from "@/api/models/referral/referral";
import { CTM_STATUS_ORDER } from "@/composables/models/treatmentPlan/useCotreatmentStatus";
import { EPatientDashboardTab, type TFilter } from "@/types/filter/tableFilters";

import {
  createQueryKeys,
  EFilterType,
  useGenericPaginatedQuery,
  type TFilterFieldMap,
  type TOwnedFilterParams,
} from "@/composables/shared/queries";

export const PatientDashboardQueryKeys = createQueryKeys(DashboardService.queryKey, (key) => ({
  dashboard: key<IPatientDashboardFilters>("dashboard"),
}));

/**
 * The plan select shows one group key; the API wants that group expanded into its member
 * treatment types. `decode` matches an existing filter array back to its group so a value
 * already sitting in `filters` (a tab slice, an injected filter) still shows in the select.
 */
const matchTreatmentGroup = (value: unknown): EActiveTreatmentFilter | null => {
  const types = value as EActiveTreatmentType[] | undefined;
  if (!types?.length) return null;

  const entries = Object.entries(ACTIVE_TREATMENT_FILTER_MAP) as [
    EActiveTreatmentFilter,
    EActiveTreatmentType[],
  ][];

  return (
    entries.find(
      ([, values]) => values.length === types.length && values.every((type) => types.includes(type))
    )?.[0] ?? null
  );
};

/**
 * UI filter fields for the all-patients dashboard.
 *
 * Declared here rather than in the page so the mapping lives next to
 * `ACTIVE_TREATMENT_FILTER_MAP`, and the page only binds the resulting models.
 */
export const patientDashboardFields = {
  activeTreatmentType: {
    type: EFilterType.SELECT,
    field: "active_treatment_types",
    encode: (value) => ACTIVE_TREATMENT_FILTER_MAP[value as EActiveTreatmentFilter],
    decode: matchTreatmentGroup,
    parse: (raw) =>
      (Object.values(EActiveTreatmentFilter) as string[]).includes(raw)
        ? (raw as EActiveTreatmentFilter)
        : null,
  },
  lastUpdate: { type: EFilterType.DATE_RANGE, field: "last_update" },
} satisfies TFilterFieldMap;

export const usePatientDashboard = (params: TOwnedFilterParams<IPatientDashboardFilters> = {}) =>
  useGenericPaginatedQuery<
    TPatientDashboardItem,
    Partial<IPatientDashboardFilters>,
    typeof patientDashboardFields
  >({
    queryKey: PatientDashboardQueryKeys.dashboard(),
    params: { ...params, fields: patientDashboardFields },
    fetcher: (fetchParams) =>
      DashboardService.dashboard({}, fetchParams.filters, fetchParams.pageParam),
  });

export { EPatientDashboardTab } from "@/types/filter/tableFilters";

/**
 * Tab definitions for the all-patients dashboard.
 * Labels are derived from the tab keys by `TableFilterTabs`
 * (`all` → `models.patient.table.predefinedFilters.all`).
 */
export const usePatientDashboardTabFilters = () =>
  computed<TFilter<EPatientDashboardTab, IPatientDashboardFilters>[]>(() => [
    {
      tab: EPatientDashboardTab.ALL,
      filters: {
        archived: false,
      },
    },
    {
      tab: EPatientDashboardTab.ARCHIVED,
      filters: {
        archived: true,
      },
    },
  ]);

/**
 * TEMPORARY MOCK — dashboard payloads carry no referral status, which is what
 * `ReferralStatusCell` renders. Until the backend exposes one, each row gets a
 * status picked deterministically from its own id, so the value is stable
 * across refreshes (and identical for every user) instead of flickering on
 * each render.
 *
 * Shared by the cotreatment dashboard and any other dashboard that needs to
 * show a status before the real field lands. Delete the whole block — plus its
 * call sites — once the payload provides one.
 */
/** Which status vocabulary the fabricated status is drawn from. */
export type TMockStatusKind = "ctm" | "referral";

const MOCK_STATUS_POOLS: Record<TMockStatusKind, readonly string[]> = {
  ctm: CTM_STATUS_ORDER,
  referral: Object.values(EReferralStatus),
};

/** djb2-style string hash — deterministic, no dependency on `Math.random()`. */
const hashString = (value: string): number => {
  let hash = 5381;

  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33 + (value.codePointAt(i) ?? 0)) % 0xff_ff_ff_ff;
  }

  return hash;
};

/**
 * Minimal shape needed to fabricate a status. Kept structural (rather than tied
 * to one dashboard's item type) so every dashboard row type satisfies it.
 */
export type TMockStatusSource = {
  /** Seeds the status — same id always yields the same status. */
  id: string;
  state?: unknown;
  last_update?: string;
  jobs_available_for_user?: unknown[];
  patient?: object | null;
};

/**
 * A `TPatient`-shaped stub carrying just the fields `ReferralStatusCell` reads:
 * `actual_treatment_plans[].referral.status`/`.status_changed_at`.
 */
export const mockStatusPatient = (
  item: TMockStatusSource,
  kind: TMockStatusKind = "referral"
): TPatient => {
  const pool = MOCK_STATUS_POOLS[kind];
  const seed = hashString(item.id ?? "");
  const status = pool[seed % pool.length];

  return {
    ...item.patient,
    actual_treatment_plans: [
      {
        id: item.id,
        state: item.state,
        jobs_available_for_user: item.jobs_available_for_user ?? [],
        referral: {
          id: item.id,
          created_at: item.last_update,
          status,
          status_changed_at: item.last_update,
        },
      },
    ],
  } as unknown as TPatient;
};

export type TActiveTreatmentTypeChipConfig = {
  labelKey: string;
  color: string;
  icon: string;
};

export const COTREATMENT_ACCENT = "#3D7EF0";
export const REFERRAL_ACCENT = "#B56CFF";

export const ACTIVE_TREATMENT_TYPE_CHIP_MAP = {
  [EActiveTreatmentType.SELECT_TREATMENT]: {
    labelKey: "models.patient.treatmentType.selectTreatment",
    color: "#469A8A",
    icon: "mdi-arrow-decision-outline",
  },

  [EActiveTreatmentType.COTREATMENT]: {
    labelKey: "generic.fields.treatmentTypes.cotreatment",
    color: COTREATMENT_ACCENT,
    icon: "mdi-account-multiple-outline",
  },

  [EActiveTreatmentType.REFERRAL]: {
    labelKey: "models.patient.treatmentType.referral",
    color: REFERRAL_ACCENT,
    icon: "mdi-account-arrow-right-outline",
  },

  [EActiveTreatmentType.REFERRAL_FREE]: {
    labelKey: "models.patient.treatmentType.referralFree",
    color: REFERRAL_ACCENT,
    icon: "mdi-tag-off-outline",
  },

  [EActiveTreatmentType.REFERRAL_INTERNAL]: {
    labelKey: "models.patient.treatmentType.referralInternal",
    color: REFERRAL_ACCENT,
    icon: "mdi-office-building-outline",
  },

  [EActiveTreatmentType.REFERRAL_PAID]: {
    labelKey: "models.patient.treatmentType.referralPaid",
    color: REFERRAL_ACCENT,
    icon: "mdi-cash-check",
  },
} satisfies Record<EActiveTreatmentType, TActiveTreatmentTypeChipConfig>;

export const DEFAULT_TREATMENT_TYPE_CHIP = {
  labelKey: "models.patient.treatmentType.none",
  color: "#BCC3CD",
  icon: "mdi-minus-circle-outline",
} satisfies TActiveTreatmentTypeChipConfig;
